import * as React from 'react';
import { Link } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid2';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import useAxios from '../utils/useAxios';
import PageShell from './layout/PageShell';
import SessionsCard from './SessionsCard';
import { SectionCard } from './ui';
import { TRUE_COLORS } from '../theme';

type Draft = { name: string; lastname: string; phone: string };

const Profile = () => {
  const axios = useAxios();
  const [profile, setProfile] = React.useState<any>(null);
  const [draft, setDraft] = React.useState<Draft>({ name: '', lastname: '', phone: '' });
  const [message, setMessage] = React.useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const toDraft = (p: any): Draft => ({ name: p?.name ?? '', lastname: p?.lastname ?? '', phone: p?.phone ?? '' });

  React.useEffect(() => {
    axios.get('management/my-membership/').then(r => { setProfile(r.data); setDraft(toDraft(r.data)); }).catch(() => {});
  }, [axios]);

  // Only owners can edit memberships through the API.
  const canEdit = !!profile?.is_patron;
  const dirty = profile && JSON.stringify(toDraft(profile)) !== JSON.stringify(draft);
  const name = [profile?.name, profile?.lastname].filter(Boolean).join(' ') || profile?.login || profile?.member?.username || '';
  const set = (k: keyof Draft) => (e: React.ChangeEvent<HTMLInputElement>) => setDraft(d => ({ ...d, [k]: e.target.value }));

  const save = async () => {
    try {
      const { data } = await axios.put(`management/members/${profile.id}/`, { name: draft.name.trim(), lastname: draft.lastname.trim(), phone: draft.phone.trim() });
      setProfile(data);
      setDraft(toDraft(data));
      setMessage({ type: 'success', text: 'Perfil actualizado.' });
    } catch {
      setMessage({ type: 'error', text: 'No se pudo actualizar tu perfil.' });
    }
  };

  return (
    <PageShell
      back={{ to: '/admin/settings', label: 'Configuración' }}
      title="Mi perfil"
      actions={canEdit && <Button variant="contained" onClick={save} disabled={!dirty}>Guardar cambios</Button>}
    >
      {message && <Alert severity={message.type} onClose={() => setMessage(null)} sx={{ mb: 3 }}>{message.text}</Alert>}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 4 }}>
          <SectionCard>
            {profile ? (
              <Stack spacing={1.5} sx={{ alignItems: 'center', textAlign: 'center', py: 2 }}>
                <Avatar sx={{ width: 72, height: 72, fontSize: '1.5rem', fontWeight: 700, bgcolor: TRUE_COLORS.primary }}>
                  {name.slice(0, 2).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '1.125rem' }}>{name}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>{profile.member?.email || profile.login || profile.member?.username}</Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  {profile.is_patron ? <Chip size="small" label="Dueño" sx={{ bgcolor: TRUE_COLORS.primary, color: '#fff' }} />
                    : profile.is_admin ? <Chip size="small" label="Administrador" /> : <Chip size="small" variant="outlined" label="Cajero" />}
                  {profile.role && <Chip size="small" variant="outlined" label={profile.role} />}
                </Stack>
              </Stack>
            ) : <Skeleton variant="rounded" height={200} />}
          </SectionCard>
        </Grid>
        <Grid size={{ xs: 12, md: 8 }}>
          <Stack spacing={3}>
            <SectionCard title="Información personal" subtitle={canEdit ? 'Así te verá tu equipo.' : 'Pide al dueño de la organización que actualice tus datos.'}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}><TextField label="Nombre" fullWidth value={draft.name} onChange={set('name')} disabled={!canEdit} /></Grid>
                <Grid size={{ xs: 12, sm: 6 }}><TextField label="Apellido" fullWidth value={draft.lastname} onChange={set('lastname')} disabled={!canEdit} /></Grid>
                <Grid size={{ xs: 12, sm: 6 }}><TextField label="Teléfono" type="tel" fullWidth value={draft.phone} onChange={set('phone')} disabled={!canEdit} /></Grid>
                <Grid size={{ xs: 12, sm: 6 }}><TextField label="Usuario" fullWidth value={profile?.login ?? profile?.member?.username ?? ''} disabled helperText="Con este usuario entras a TRUE POS de tu negocio." /></Grid>
              </Grid>
            </SectionCard>
            <SectionCard title="Seguridad">
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ justifyContent: 'space-between', alignItems: { sm: 'center' } }}>
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Contraseña</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>Te enviaremos un enlace a tu correo para cambiarla.</Typography>
                </Box>
                <Button variant="outlined" component={Link} to="/request-password-reset">Cambiar contraseña</Button>
              </Stack>
            </SectionCard>
            <SessionsCard />
          </Stack>
        </Grid>
      </Grid>
    </PageShell>
  );
};

export default Profile;
